import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import {
    AccountTicketsFacade,
    NotificationFacade,
    RelatedCardFacade,
    RelatedCardUserActions
} from '@ridango/components';
import { AppStartupService, RelatedCard } from '@ridango/core';
import Env from '@ridango/core/env';
import { CardActionsFeature, ThemeFeature } from '@ridango/core/lib/enums/features.enum';
import { AccountTicketListType } from '@ridango/widgets';

import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    templateUrl: './related-card-detail-page.component.html',
    styleUrls: ['./related-card-detail-page.component.scss']
})
export class RelatedCardDetailPageComponent implements OnInit, OnDestroy {
    private componentDestroyed$ = new Subject<void>();
    ticketListTypes = AccountTicketListType;
    relatedCardsLoaded$: Observable<boolean> = this.relatedCardFacade.loaded$;
    selectedRelatedCard$: Observable<RelatedCard> = this.relatedCardFacade.selectedCard$;
    themeFeature = ThemeFeature;
    constructor(
        @Inject('ENV') public environment: Env,
        public translate: TranslateService,
        public route: ActivatedRoute,
        private router: Router,
        private relatedCardFacade: RelatedCardFacade,
        private accountTicketsFacade: AccountTicketsFacade,
        private appConfig: AppStartupService,
        private notification: NotificationFacade
    ) {}

    ngOnInit() {
        // casting url param to number
        const id: number = +this.route.snapshot.params.id;
        this.selectedRelatedCard$.pipe(takeUntil(this.componentDestroyed$)).subscribe((card: RelatedCard) => {
            if (!card) {
                this.relatedCardFacade.fetchAllRelatedCards();
            } else {
                if (card.id !== id) {
                    // link id and selected card in state not matching, send user back to list
                    this.back();
                }
            }
        });

        this.relatedCardsLoaded$.pipe(takeUntil(this.componentDestroyed$)).subscribe((loaded) => {
            if (loaded) {
                this.relatedCardFacade
                    .getRelatedCardById(id)
                    .pipe(takeUntil(this.componentDestroyed$))
                    .subscribe((card: RelatedCard) => {
                        if (card) {
                            this.relatedCardFacade.setSelectedRelatedCard(card);
                            this.relatedCardFacade.setSelectedCardActions(this.getCardActions(card));
                            if (!card.blocked) {
                                this.accountTicketsFacade.fetchRelatedCardTickets(card);
                            }
                        } else {
                            this.back();
                        }
                    });
            }
        });
    }

    ngOnDestroy() {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
        this.relatedCardFacade.setSelectedRelatedCard(null);
    }

    back() {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
        this.relatedCardFacade.setSelectedRelatedCard(null);
        this.router.navigate([this.translate.currentLang, 'account', 'related-cards']);
    }

    navigate(to) {
        this.router.navigate(to);
    }

    actionSelected(action: string) {
        switch (action) {
            case RelatedCardUserActions.EDIT_DATA:
                this.router.navigate([
                    this.translate.currentLang,
                    'account',
                    'related-cards',
                    +this.route.snapshot.params.id,
                    'edit'
                ]);
        }
    }

    getCardActions(card) {
        const actions = [];
        if (!card.blocked) {
            if (this.environment.RELATED_CARD.ACTIONS.includes(CardActionsFeature.ADD_MONEY)) {
                actions.push(RelatedCardUserActions.ADD_MONEY);
            }
            actions.push(RelatedCardUserActions.EDIT_DATA);
        }
        const cardBlockingAllowed =
            this.appConfig.getPropertyByCode('BLOCK_CARD') &&
            this.appConfig.getPropertyByCode('BLOCK_CARD').value === 'Y';
        if (cardBlockingAllowed) {
            if (card.blocked) {
                this.setCardAlerts(card);
                if (['C', 'N'].includes(card.blocked) && this.environment.RELATED_CARD.ALLOW_UNBLOCK) {
                    actions.push(RelatedCardUserActions.UNBLOCK);
                }
            } else {
                actions.push(RelatedCardUserActions.BLOCK);
            }
        }
        actions.push(RelatedCardUserActions.REMOVE);
        return actions;
    }

    setCardAlerts(card) {
        if (card.blocked === 'L') {
            this.notification.info(this.translate.instant('LOCAL_RELATED_CARD_BLOCKED_CARD_CONTACT_SUPPORT'), {
                title: 'information',
                showAfterRouteChange: false
            });
        } else {
            this.notification.info(this.translate.instant('LOCAL_RELATED_CARD_DETAIL_THIS_CARD_IS_BLOCKED'), {
                title: 'information',
                showAfterRouteChange: false
            });
        }
    }

    navigateToCardList() {
        this.router.navigate([this.translate.currentLang, 'account', 'related-cards']);
    }
}
